import { Fragment } from 'react';
import { cx } from '../cx';
import {
  DropdownMenu,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownSeparator,
} from '../DropdownMenu/DropdownMenu';
import { Icon } from '../Icon/Icon';

/**
 * A POP-UP BUTTON (components page section 1; HIG Pop-up buttons, hig:
 * https://developer.apple.com/design/human-interface-guidelines/pop-up-buttons; brief
 * FE-FOUNDATION section 10).
 *
 * A button that shows the current choice and opens a menu of the others, with a tick on the
 * current one. Two contexts, one construction:
 *  - `toolbar`: the window toolbar's glass capsule ("Owner: All"; L9 glass only on the
 *    toolbar and the menu).
 *  - `content`: the content-area pop-up the product already draws for the activity stream
 *    and the Dashboard's sort (`.record-popup`; its kit master is cited where it is drawn).
 * Both carry the up-and-down chevron pair the account button and Settings pop-ups use
 * (`SettingsGroup.tsx`), which replaces the mockup's "▾".
 *
 * The menu is the product's (`ui/DropdownMenu`, one radio group, separators, counts in the
 * secondary ink after a middle dot, "Artur · 41"). An option list with ONE option is no
 * choice, and the control stays where it is, dimmed and disabled, with its face (decision page
 * item 12.10 answered B, Q-FND-5: "Ein Menue mit nur einer Option bleibt sichtbar, abgeblendet";
 * a control used every day does not vanish because there is nothing to pick right now). The
 * dimmed look is the product's disabled look (`disabled:opacity-50`, `Button`'s BASE; Q-FND-3).
 * With no option at all there is no value to show and nothing is drawn.
 */
export interface PopUpOption {
  readonly value: string;
  readonly label: string;
  /** Rows this option shows on the calling page; absent while unknown (loading, failed). */
  readonly count?: number | undefined;
  /** A separator above this option (components page: after "My clients"). */
  readonly separatorBefore?: boolean | undefined;
}

export interface PopUpButtonProps {
  readonly context: 'toolbar' | 'content';
  /** The introductory label, "Owner:" (HIG). Absent shows the value alone. */
  readonly label?: string | undefined;
  readonly value: string;
  /** What the face reads for the current value; defaults to the option's label. */
  readonly valueText?: string | undefined;
  readonly options: readonly PopUpOption[];
  readonly onValueChange: (value: string) => void;
  /** The control's accessible name when the label alone does not say it. */
  readonly ariaLabel?: string | undefined;
}

/** The chevron pair: the menu opens, it does not drop down a list. */
function Chevrons({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx('inline-flex shrink-0 flex-col items-center gap-px', className)}
    >
      <Icon name="chevron-up" size="0.5rem" />
      <Icon name="chevron-down" size="0.5rem" />
    </span>
  );
}

export function PopUpButton({
  context,
  label,
  value,
  valueText,
  options,
  onValueChange,
  ariaLabel,
}: PopUpButtonProps) {
  if (options.length === 0) return null;
  const current = options.find((option) => option.value === value);
  const face = valueText ?? current?.label ?? '';
  const single = options.length === 1;
  const trigger =
    context === 'toolbar' ? (
      <button
        type="button"
        data-popup-button="toolbar"
        aria-label={ariaLabel}
        disabled={single}
        className={cx(
          'glass-control hit-expand inline-flex h-(--shell-toolbar-item) cursor-default items-center gap-2',
          'rounded-(--radius-button) px-3 text-sm text-ink disabled:opacity-50',
        )}
      >
        <span className="whitespace-nowrap">{label === undefined ? face : `${label} ${face}`}</span>
        <Chevrons className="text-ink-muted" />
      </button>
    ) : (
      // kit: Apple macOS 27 UI Kit.sketch / Pop-up Buttons/Light/Content Area/3 Rg/1 - Idle,
      // the master `.record-popup` and its `--record-popup-*` tokens are read off.
      <button
        type="button"
        data-popup-button="content"
        aria-label={ariaLabel}
        disabled={single}
        className="record-popup cursor-default disabled:opacity-50"
      >
        <span className="whitespace-nowrap">{label === undefined ? face : `${label} ${face}`}</span>
        <Chevrons className="text-ink-muted" />
      </button>
    );
  if (single) return trigger;
  return (
    <DropdownMenu align={context === 'toolbar' ? 'end' : 'start'} trigger={trigger}>
      <DropdownRadioGroup value={value} onValueChange={onValueChange}>
        {options.map((option) => (
          <Fragment key={option.value}>
            {option.separatorBefore === true && <DropdownSeparator />}
            <DropdownRadioItem value={option.value}>
              <span data-popup-option={option.value}>
                {option.label}
                {option.count !== undefined && (
                  <span className="text-ink-muted"> · {option.count.toLocaleString('en-US')}</span>
                )}
              </span>
            </DropdownRadioItem>
          </Fragment>
        ))}
      </DropdownRadioGroup>
    </DropdownMenu>
  );
}
